import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";
import { FadeUp } from "./Reveal";

const EXP_CHOICES = ["Moins de 2 ans", "2 à 5 ans", "5 à 10 ans", "Plus de 10 ans"];
const BTP_CHOICES = ["Oui, très bien", "Oui, partiellement", "Non, mais forte expérience commerciale"];

const INITIAL = {
  nom: "", prenom: "", telephone: "", email: "", code_postal: "", ville: "",
  secteur: "", experience: "", btp: "", presentation: "", linkedin: "",
  rgpd: false, vivier: false, website: "",
};

export const CandidatureForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, String(v)));
      await axios.post(`${API}/candidatures`, data);
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Vérifiez les champs du formulaire puis réessayez."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-[2rem] border border-petrol/10 shadow-xl shadow-petrol/5 p-7 sm:p-10">
      {sent ? (
        <div data-testid="candidature-form-success" className="text-center py-14">
          <span className="mx-auto w-16 h-16 rounded-t-full rounded-b-2xl bg-sagelight flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-teal" />
          </span>
          <h3 className="font-display font-medium text-petrol text-2xl sm:text-3xl mb-4">
            Candidature transmise.
          </h3>
          <p className="text-mute text-base max-w-md mx-auto leading-relaxed">
            Votre candidature a bien été transmise à EQUORIS.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="candidature-form">
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={set("website")}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="cand-nom" className="eq-label">Nom *</label>
              <input id="cand-nom" data-testid="cand-nom" className="eq-input" value={form.nom} onChange={set("nom")} required />
            </div>
            <div>
              <label htmlFor="cand-prenom" className="eq-label">Prénom *</label>
              <input id="cand-prenom" data-testid="cand-prenom" className="eq-input" value={form.prenom} onChange={set("prenom")} required />
            </div>
            <div>
              <label htmlFor="cand-telephone" className="eq-label">Téléphone *</label>
              <input id="cand-telephone" data-testid="cand-telephone" type="tel" className="eq-input" value={form.telephone} onChange={set("telephone")} required />
            </div>
            <div>
              <label htmlFor="cand-email" className="eq-label">Email *</label>
              <input id="cand-email" data-testid="cand-email" type="email" className="eq-input" value={form.email} onChange={set("email")} required />
            </div>
            <div>
              <label htmlFor="cand-code-postal" className="eq-label">Code postal *</label>
              <input id="cand-code-postal" data-testid="cand-code-postal" inputMode="numeric" className="eq-input" value={form.code_postal} onChange={set("code_postal")} required />
            </div>
            <div>
              <label htmlFor="cand-ville" className="eq-label">Ville *</label>
              <input id="cand-ville" data-testid="cand-ville" className="eq-input" value={form.ville} onChange={set("ville")} required />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="cand-secteur" className="eq-label">Secteur géographique souhaité *</label>
              <input id="cand-secteur" data-testid="cand-secteur" className="eq-input" placeholder="Exemple : Pays de la Loire, Bretagne, Grand Ouest…" value={form.secteur} onChange={set("secteur")} required />
            </div>
            <div>
              <label htmlFor="cand-experience" className="eq-label">Expérience commerciale *</label>
              <select id="cand-experience" data-testid="cand-experience" className="eq-input" value={form.experience} onChange={set("experience")} required>
                <option value="" disabled>Sélectionnez</option>
                {EXP_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="cand-btp" className="eq-label">Connaissez-vous le secteur BTP / matériaux ? *</label>
              <select id="cand-btp" data-testid="cand-btp" className="eq-input" value={form.btp} onChange={set("btp")} required>
                <option value="" disabled>Sélectionnez</option>
                {BTP_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="cand-presentation" className="eq-label">Parlez-nous de votre parcours commercial *</label>
            <textarea
              id="cand-presentation"
              data-testid="cand-presentation"
              rows={4}
              className="eq-input resize-y"
              placeholder="Vos expériences, votre clientèle, vos résultats ou ce qui vous motive à rejoindre EQUORIS…"
              value={form.presentation}
              onChange={set("presentation")}
              required
            />
          </div>

          <div className="mt-5">
            <label htmlFor="cand-linkedin" className="eq-label">Profil LinkedIn / lien professionnel (facultatif)</label>
            <input
              id="cand-linkedin"
              data-testid="cand-linkedin"
              type="url"
              className="eq-input"
              placeholder="https://www.linkedin.com/in/votre-profil"
              value={form.linkedin}
              onChange={set("linkedin")}
            />
          </div>

          <div className="mt-6 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="cand-rgpd-label">
              <input
                type="checkbox"
                data-testid="cand-rgpd"
                checked={form.rgpd}
                onChange={set("rgpd")}
                required
                className="mt-0.5 w-4 h-4 rounded accent-[#1A6B78]"
              />
              <span className="text-sm text-encre/85">
                J'accepte que les informations transmises soient utilisées par EQUORIS dans le
                cadre de l'étude de ma candidature. *
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="cand-vivier-label">
              <input
                type="checkbox"
                data-testid="cand-vivier"
                checked={form.vivier}
                onChange={set("vivier")}
                className="mt-0.5 w-4 h-4 rounded accent-[#1A6B78]"
              />
              <span className="text-sm text-encre/85">
                J'accepte que ma candidature soit conservée afin qu'EQUORIS puisse me
                recontacter pour de futures opportunités. (facultatif)
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="candidature-form-submit"
            className="mt-8 w-full rounded-full bg-petrol text-ivoire text-sm font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 transition-all duration-300 hover:bg-teal disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Envoi en cours…" : "Envoyer ma candidature"}
            {!sending && <Send className="w-4 h-4" />}
          </button>

          <p className="mt-6 text-xs text-pierre leading-relaxed" data-testid="cand-rgpd-notice">
            Les informations recueillies sont destinées exclusivement aux personnes chargées
            du recrutement chez EQUORIS, pour l'étude de votre candidature. Si votre
            candidature n'est pas retenue, elle ne sera conservée dans un vivier qu'avec votre
            accord, pour une durée maximale de 2 ans à compter du dernier contact. Vous
            disposez de droits d'accès, de rectification, d'effacement et d'opposition :
            contact@equoris.fr.
          </p>
        </form>
      )}
    </div>
  );
};
